import React from 'react';
import { MilanGradientColors, S4WhyChoose, S4WhyChooseProps, MilanTextColors } from '@milanlaser/mlcomponents';
import { PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import { processBackgroundAndGradientColorMap } from '../../../../components/builder/utils/builder.utils';
import { getLocationContext } from '../../../../utils/general/getLocationContext';
import { useFirebaseMetrics } from '../../utils/useFirebaseMetrics';
import type { LocationBuilderState } from '../../../../utils/general/builder';

type BuilderState<T> = {
    device: string;
    deviceSize: string;
    isBrowser: boolean;
    isServer: boolean;
} & T;

type BuilderProps<T> = {
    builderState: {
        state?: BuilderState<T>;
    };
};

// 1) Omit both the gradient _and_ the padding props from the original
// 2) Then re-add them (along with our Builder fields and any other overrides)
export type S4WhyChooseArgs = BuilderProps<{
    model: string;
}> &
    Omit<
        S4WhyChooseProps,
        'backgroundGradient' | 'desktopPaddingTop' | 'desktopPaddingBottom' | 'mobilePaddingTop' | 'mobilePaddingBottom'
    > & {
        desktopPaddingTop: PaddingSize;
        desktopPaddingBottom: PaddingSize;
        mobilePaddingTop: PaddingSize;
        mobilePaddingBottom: PaddingSize;
        backgroundGradient: MilanGradientColors | 'None';
        imageFlip?: boolean;
        buttons?: { url: string; label: string; variant: string }[];
        links?: { linkText: string; linkUrl: string; linkTextColor: string }[];
        variant?: 'default' | 'brandPartner';
        textSteps?: { title: string; description: string }[];
    };

export function S4WhyChooseSection(props: S4WhyChooseArgs) {
    const { replaceVariables } = useFirebaseMetrics();
    const {
        builderState,
        desktopPaddingTop,
        desktopPaddingBottom,
        mobilePaddingTop,
        mobilePaddingBottom,
        backgroundGradient,
        imageFlip = false,
        buttons,
        links,
        variant = 'default',
        textSteps,
        ...restProps
    } = props;

    const isStatePage = builderState.state?.model === 'state-page';
    const isMetroPage = builderState.state?.model === 'metro-page';
    const isClinicPage = builderState.state?.model === 'clinic-page';
    const locationName = getLocationContext(builderState as LocationBuilderState);

    const headlineOverride =
        isStatePage || isMetroPage || isClinicPage
            ? `Why Choose Milan Laser for Hair Removal in ${locationName}`
            : restProps.headline;

    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;

    const textPosition = imageFlip === true ? 'right' : 'left'; // This is the value for textPosition in S4WhyChoose component because its a dropdown prop.
    const BgGradientColor = backgroundGradient === 'None' ? 'default' : backgroundGradient; // This is the value for backgroundGradient in S4WhyChoose component because its a dropdown prop.

    const transformedButtons =
        props.buttons
            ?.filter((btn): btn is { url: string; label: string } => Boolean(btn.url))
            .map(({ url, label }) => ({
                url, // now url is definitely string
                label,
                onClick: () => (window.location.href = url),
                target: '_self' as const,
            })) ?? [];

    const link = links?.[0];
    const linkText = link?.linkText;
    const linkUrl = link?.linkUrl;
    const linkTextColor = link?.linkTextColor as MilanTextColors;

    // Process text fields with Firebase metrics
    const processedText = replaceVariables(restProps.text);
    const processedOptionalText = replaceVariables(restProps.optionalText);
    const processedIcons =
        restProps.icons?.map((icon) => ({
            ...icon,
            iconText: replaceVariables(icon.iconText),
        })) || [];
    const processedTextSteps =
        textSteps?.map((step) => ({
            ...step,
            title: replaceVariables(step.title),
            description: replaceVariables(step.description),
        })) || [];

    const modifiedProps = {
        ...restProps,
        text: processedText,
        optionalText: processedOptionalText,
        icons: processedIcons,
        buttons: transformedButtons,
        linkText,
        linkUrl,
        linkTextColor,
        variant,
        textSteps: processedTextSteps,
        className: `mcl-mx-auto ${topPaddingClasses} ${bottomPaddingClasses}`,
        backgroundGradient: BgGradientColor, // This is the value for backgroundGradient in S4WhyChoose component because we are using 'None' value in builder, however, the component expects a 'default' which is  type MilanGradientColors.
    }; // Adding mcl-max-w-7xl to limit the width of the component to match the nav bar for consistency

    //get the background color and gradient color for the wrapper div in builder.
    const backgroundColor = processBackgroundAndGradientColorMap({
        backgroundColor: props.backgroundColor || 'default',
        gradientColor: props.backgroundGradient || 'default',
    });

    return (
        <div className={`${backgroundColor}`}>
            <S4WhyChoose {...modifiedProps} textPosition={textPosition} headline={headlineOverride} />
        </div>
    );
}
